/**
 * TimeSeries — minimal SVG multi-line chart.
 *
 * Used by the Trends module to overlay per-district weekly attendance
 * against the national campaign aggregate.
 *
 * Design notes:
 *  - Y-axis is shared across all series and scaled to the global max.
 *  - `direction: ltr` on the SVG keeps the internal coordinate system
 *    intuitive (x grows left→right); the chart still sits inside an
 *    RTL document. To mirror the prototype's "latest week on the LEFT"
 *    convention in RTL, points and labels are rendered in reverse —
 *    index 0 lives on the visual right, the last index on the visual
 *    left. Pass `rtl={false}` to disable.
 *  - Emphasised series render on top with a thicker stroke. The trend
 *    classifier modulates the colour of the end-cap circle.
 *  - Confidence/CI bands are explicitly out of scope here (those live
 *    on the noshow chart).
 */
import { useMemo } from 'react';

export type TrendClassifier = 'accelerating' | 'stable' | 'stalling';

export interface TimeSeriesPoint {
  /** Stable id for React keys + legend toggles. */
  id: string;
  label: string;
  /** Y-values aligned by index to `xLabels`. */
  points: number[];
  color?: string;
  /** Thicker stroke, rendered on top, end-cap marker shown. */
  emphasized?: boolean;
  /** Classifier — modulates the end-cap colour. */
  trend?: TrendClassifier;
  /** Optional dash pattern (e.g. for the campaign baseline). */
  dashed?: boolean;
}

export interface TimeSeriesProps {
  series: TimeSeriesPoint[];
  xLabels: string[];
  height?: number;
  /**
   * When true (default), the visual x-axis is RTL: index 0 sits on the
   * right, the last index on the left. This matches the Hebrew reading
   * order in the prototype where the latest week is read first.
   */
  rtl?: boolean;
}

const W = 940;
const PAD = { l: 44, r: 24, t: 16, b: 28 } as const;
const TICKS = 4;

function classifierColor(trend?: TrendClassifier): string | undefined {
  switch (trend) {
    case 'accelerating': return 'var(--positive, #0f7a48)';
    case 'stalling':     return 'var(--warning, #b45309)';
    case 'stable':       return 'var(--ink-3, #6b7280)';
    default:             return undefined;
  }
}

/**
 * Format a y-axis tick value compactly (1.2K, 12, 0.5K).
 */
function fmtTick(v: number): string {
  if (v >= 1000) return `${(v / 1000).toFixed(1)}K`;
  return String(Math.round(v));
}

export function TimeSeries({
  series,
  xLabels,
  height = 320,
  rtl = true,
}: TimeSeriesProps) {
  const h = height;
  const innerW = W - PAD.l - PAD.r;
  const innerH = h - PAD.t - PAD.b;

  // Global y-max across every visible series; 15% headroom so the top
  // line is not flush with the chart edge.
  const allValues = useMemo(
    () => series.flatMap((s) => s.points),
    [series],
  );
  const rawMax = allValues.length > 0 ? Math.max(...allValues) : 1;
  const max = Math.max(1, rawMax * 1.15);

  const xStep = xLabels.length > 1 ? innerW / (xLabels.length - 1) : innerW;

  // RTL mapping — index i shows up at the (n-1-i)th visual slot.
  const xForIndex = (i: number) => {
    const slot = rtl ? xLabels.length - 1 - i : i;
    return PAD.l + slot * xStep;
  };
  const yForValue = (v: number) =>
    PAD.t + innerH - (v / max) * innerH;

  const tickValues = Array.from({ length: TICKS + 1 }, (_, i) =>
    (max * i) / TICKS,
  );

  // Render emphasised series LAST so they sit on top of the rest.
  const sorted = useMemo(() => {
    const list = [...series];
    list.sort((a, b) => Number(!!a.emphasized) - Number(!!b.emphasized));
    return list;
  }, [series]);

  return (
    <svg
      viewBox={`0 0 ${W} ${h}`}
      width="100%"
      height={h}
      preserveAspectRatio="none"
      style={{ direction: 'ltr' }}
      role="img"
      aria-label="מגמת השתתפות שבועית"
    >
      {/* Gridlines + y-axis tick labels. */}
      {tickValues.map((v, i) => (
        <g key={`grid-${i}`}>
          <line
            x1={PAD.l}
            x2={W - PAD.r}
            y1={yForValue(v)}
            y2={yForValue(v)}
            stroke="var(--hairline, #e5e5e5)"
          />
          <text
            x={PAD.l - 8}
            y={yForValue(v) + 4}
            textAnchor="end"
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 10,
              fill: 'var(--ink-3)',
            }}
          >
            {fmtTick(v)}
          </text>
        </g>
      ))}

      {/* X-axis tick labels — rendered in the same RTL slot the points use. */}
      {xLabels.map((label, i) => (
        <text
          key={`x-${i}`}
          x={xForIndex(i)}
          y={h - PAD.b + 16}
          textAnchor="middle"
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 10,
            fill: 'var(--ink-3)',
          }}
        >
          {label}
        </text>
      ))}

      {/* Series — paths first, then end-cap markers. */}
      {sorted.map((s) => {
        const stroke = s.color ?? 'var(--c-navy, #0b1220)';
        const strokeWidth = s.emphasized ? 2.5 : 1.25;
        const dash = s.dashed ? '3 3' : undefined;
        const opacity = s.emphasized ? 1 : 0.78;

        if (!s.points.length) return null;
        const d = s.points
          .map(
            (v, i) => `${i === 0 ? 'M' : 'L'}${xForIndex(i)},${yForValue(v)}`,
          )
          .join(' ');

        // End-cap sits at the LAST data index (visually leftmost in RTL).
        const lastIdx = s.points.length - 1;
        const lastVal = s.points[lastIdx] ?? 0;
        const capX = xForIndex(lastIdx);
        const capY = yForValue(lastVal);
        const capColor =
          (s.emphasized ? classifierColor(s.trend) : undefined) ?? stroke;

        return (
          <g key={s.id} opacity={opacity}>
            <path
              d={d}
              fill="none"
              stroke={stroke}
              strokeWidth={strokeWidth}
              strokeDasharray={dash}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            {s.emphasized && (
              <circle cx={capX} cy={capY} r={3.5} fill={capColor} />
            )}
          </g>
        );
      })}
    </svg>
  );
}
